import React, { useState } from 'react';
import { 
  Key, 
  Copy, 
  Check, 
  RefreshCw, 
  ExternalLink, 
  Calendar, 
  ShieldCheck, 
  Radio, 
  Database, 
  Globe, 
  Sparkles,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

interface ApiKeyRecord {
  id: string;
  name: string;
  keyPrefix: string;
  scope: 'read-only' | 'sync-write' | 'admin';
  createdAt: string;
  lastUsed?: string;
}

export default function ApiIntegrationsManager() {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; latency?: number } | null>(null);
  const [targetSfiUrl, setTargetSfiUrl] = useState('https://db.sfi.org/api/v1/chapters/zavala');
  const [chapterSfiId, setChapterSfiId] = useState('R03-ZAVALA-74211');
  
  const [apiKeys, setApiKeys] = useState<ApiKeyRecord[]>([
    {
      id: 'key-1',
      name: 'STARFLEET HQ Chapter Sync Service',
      keyPrefix: 'sfi_live_9a7f...',
      scope: 'sync-write',
      createdAt: '2026-01-15',
      lastUsed: '2026-09-20 18:42 UTC'
    },
    {
      id: 'key-2',
      name: 'Public Calendar iCal Subscriber (Region 3)',
      keyPrefix: 'sfi_pub_3b1c...',
      scope: 'read-only',
      createdAt: '2026-03-10',
      lastUsed: '2026-09-21 08:14 UTC'
    }
  ]);

  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScope, setNewKeyScope] = useState<'read-only' | 'sync-write' | 'admin'>('read-only');
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  const baseUrl = window.location.origin;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    toast.success(`Copied ${label} to clipboard!`);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const handleCreateApiKey = () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter an application or partner name for the API key');
      return;
    }

    const randomSuffix = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    const fullKey = `lcars_${newKeyScope === 'admin' ? 'adm' : newKeyScope === 'sync-write' ? 'sync' : 'ro'}_${randomSuffix}`;

    const newRecord: ApiKeyRecord = {
      id: `key-${Date.now()}`,
      name: newKeyName.trim(),
      keyPrefix: fullKey.slice(0, 14) + '...',
      scope: newKeyScope,
      createdAt: new Date().toISOString().split('T')[0],
      lastUsed: 'Never'
    };

    setApiKeys([newRecord, ...apiKeys]);
    setGeneratedKey(fullKey);
    setNewKeyName('');
    toast.success('API Key generated successfully! Be sure to copy it now.');
  };

  const handleTestSfiConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);

    try {
      const response = await fetch('/api/v1/sync/outbound-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'STARFLEET-DATABASE-RELAY-2026'
        },
        body: JSON.stringify({ targetUrl: targetSfiUrl })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setTestResult({
          success: true,
          message: `Connected successfully to STARFLEET relay at ${data.target}! HTTP status ${data.status}`,
          latency: data.latencyMs
        });
        toast.success('STARFLEET database handshake successful!');
      } else {
        setTestResult({
          success: false,
          message: data.error || 'Connection failed or target is unreachable.'
        });
        toast.error('STARFLEET connection handshake failed.');
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Network error attempting outbound connection test.'
      });
      toast.error('Failed to dispatch connection test.');
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-blue pl-4 lg:pl-6 py-2">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-lcars-blue uppercase tracking-wider flex items-center gap-2">
              <Database className="w-6 h-6 text-lcars-gold" />
              Subspace API & STARFLEET Database Relay
            </h2>
            <p className="text-xs text-[var(--foreground)]/50 uppercase tracking-widest font-mono mt-1">
              Bi-Directional REST Architecture • RFC 5545 iCalendar Feeds • Secure Key Management
            </p>
          </div>
          <span className="hidden sm:inline-block bg-lcars-blue/20 text-lcars-blue border border-lcars-blue/40 px-3 py-1 rounded-full text-xs font-mono font-bold">
            RELAY ACTIVE: v1.6.0
          </span>
        </div>
      </div>

      {/* Grid: Left Column = Endpoints & Feeds, Right Column = STARFLEET Sync */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Outbound & Inbound Endpoints Card */}
        <div className="bg-black/40 border-2 border-lcars-gold rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 border-b border-white/10 pb-3">
            <Globe className="w-5 h-5 text-lcars-gold" />
            <h3 className="font-bold text-lg text-lcars-gold uppercase tracking-wide">
              Connected Endpoints & Feeds
            </h3>
          </div>

          <p className="text-xs text-[var(--foreground)]/70">
            Share these endpoints with STARFLEET International headquarters, sister chapters, or calendar applications to stream real-time events and roster verification.
          </p>

          <div className="space-y-3">
            {/* Live iCalendar Feed */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-lcars-tan uppercase flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-lcars-gold" />
                  Live iCalendar (.ics) Subscription Feed
                </span>
                <span className="text-[0.65rem] bg-lcars-blue/20 text-lcars-blue px-2 py-0.5 rounded font-bold uppercase">
                  Public
                </span>
              </div>
              <p className="text-[0.7rem] text-[var(--foreground)]/50">
                Subscribe in Apple Calendar, Google Calendar, or Outlook to sync all USS Zavala missions automatically.
              </p>
              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="text" 
                  readOnly 
                  value={`${baseUrl}/api/v1/events/feed.ics`}
                  className="flex-1 bg-black/60 border border-white/20 rounded px-2.5 py-1.5 text-xs font-mono text-lcars-gold selection:bg-lcars-gold selection:text-black"
                />
                <button 
                  onClick={() => copyToClipboard(`${baseUrl}/api/v1/events/feed.ics`, 'iCal Feed URL')}
                  className="lcars-pill bg-lcars-gold text-black px-3 py-1.5 text-xs font-bold flex items-center gap-1"
                >
                  {copiedField === 'iCal Feed URL' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  COPY
                </button>
              </div>
            </div>

            {/* REST Events JSON Feed */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-lcars-tan uppercase flex items-center gap-1.5">
                  <Radio className="w-4 h-4 text-lcars-blue" />
                  Missions & Events JSON Endpoint
                </span>
                <span className="text-[0.65rem] bg-lcars-blue/20 text-lcars-blue px-2 py-0.5 rounded font-bold uppercase">
                  GET /api/v1/events
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="text" 
                  readOnly 
                  value={`${baseUrl}/api/v1/events`}
                  className="flex-1 bg-black/60 border border-white/20 rounded px-2.5 py-1.5 text-xs font-mono text-[var(--foreground)]"
                />
                <button 
                  onClick={() => copyToClipboard(`${baseUrl}/api/v1/events`, 'Events Endpoint')}
                  className="lcars-pill bg-lcars-tan text-black px-3 py-1.5 text-xs font-bold flex items-center gap-1"
                >
                  {copiedField === 'Events Endpoint' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  COPY
                </button>
              </div>
            </div>

            {/* Authenticated Roster Endpoint */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-lcars-tan uppercase flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-lcars-pink" />
                  Crew Roster Sync Endpoint (Secure)
                </span>
                <span className="text-[0.65rem] bg-lcars-pink/20 text-lcars-pink px-2 py-0.5 rounded font-bold uppercase">
                  API Key Required
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="text" 
                  readOnly 
                  value={`${baseUrl}/api/v1/roster`}
                  className="flex-1 bg-black/60 border border-white/20 rounded px-2.5 py-1.5 text-xs font-mono text-[var(--foreground)]"
                />
                <button 
                  onClick={() => copyToClipboard(`${baseUrl}/api/v1/roster`, 'Roster Endpoint')}
                  className="lcars-pill bg-lcars-tan text-black px-3 py-1.5 text-xs font-bold flex items-center gap-1"
                >
                  {copiedField === 'Roster Endpoint' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  COPY
                </button>
              </div>
            </div>

            {/* Inbound STARFLEET Webhook Endpoint */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-lcars-tan uppercase flex items-center gap-1.5">
                  <RefreshCw className="w-4 h-4 text-lcars-blue" />
                  Inbound STARFLEET Webhook Receiver
                </span>
                <span className="text-[0.65rem] bg-lcars-gold/20 text-lcars-gold px-2 py-0.5 rounded font-bold uppercase">
                  POST /api/v1/sync/starfleet
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="text" 
                  readOnly 
                  value={`${baseUrl}/api/v1/sync/starfleet`}
                  className="flex-1 bg-black/60 border border-white/20 rounded px-2.5 py-1.5 text-xs font-mono text-[var(--foreground)]"
                />
                <button 
                  onClick={() => copyToClipboard(`${baseUrl}/api/v1/sync/starfleet`, 'Webhook Endpoint')}
                  className="lcars-pill bg-lcars-tan text-black px-3 py-1.5 text-xs font-bold flex items-center gap-1"
                >
                  {copiedField === 'Webhook Endpoint' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  COPY
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* STARFLEET Database Outbound Connection Settings */}
        <div className="bg-black/40 border-2 border-lcars-blue rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 border-b border-white/10 pb-3">
            <Radio className="w-5 h-5 text-lcars-blue" />
            <h3 className="font-bold text-lg text-lcars-blue uppercase tracking-wide">
              STARFLEET International DB Link
            </h3>
          </div>

          <p className="text-xs text-[var(--foreground)]/70">
            Configure automated outbound synchronization with STARFLEET International headquarters database to verify SCC membership numbers and expiration dates.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                STARFLEET API Gateway Endpoint URL
              </label>
              <input 
                type="url" 
                value={targetSfiUrl} 
                onChange={e => setTargetSfiUrl(e.target.value)}
                className="w-full bg-black/60 border border-white/20 rounded-lg p-2.5 text-xs text-[var(--foreground)] font-mono focus:border-lcars-blue outline-none"
                placeholder="https://db.sfi.org/api/v1/..."
              />
            </div>

            <div>
              <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                USS Zavala STARFLEET Chapter ID
              </label>
              <input 
                type="text" 
                value={chapterSfiId} 
                onChange={e => setChapterSfiId(e.target.value)}
                className="w-full bg-black/60 border border-white/20 rounded-lg p-2.5 text-xs text-[var(--foreground)] font-mono focus:border-lcars-blue outline-none"
                placeholder="e.g. R03-ZAVALA-74211"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                onClick={handleTestSfiConnection}
                disabled={testingConnection}
                className="lcars-pill bg-lcars-blue text-black font-bold px-5 py-2 text-xs flex items-center justify-center gap-2 hover:bg-lcars-blue/80 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${testingConnection ? 'animate-spin' : ''}`} />
                {testingConnection ? 'PINGING STARFLEET...' : 'TEST SFI DB CONNECTION'}
              </button>

              <button 
                onClick={() => toast.success('STARFLEET integration settings saved successfully.')}
                className="lcars-pill bg-lcars-tan text-black font-bold px-5 py-2 text-xs hover:bg-lcars-tan/80"
              >
                SAVE SETTINGS
              </button>
            </div>

            {testResult && (
              <div className={`p-3 rounded-lg border flex items-start gap-2.5 text-xs ${
                testResult.success 
                  ? 'bg-lcars-blue/10 border-lcars-blue/40 text-lcars-blue' 
                  : 'bg-lcars-red/10 border-lcars-red/40 text-lcars-red'
              }`}>
                {testResult.success ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="font-bold">{testResult.message}</p>
                  {testResult.latency !== undefined && (
                    <p className="text-[0.65rem] opacity-75 font-mono mt-0.5">Roundtrip Latency: {testResult.latency}ms</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* API Key Management Section */}
      <div className="bg-black/40 border-2 border-lcars-pink rounded-xl p-5 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-lcars-pink" />
            <h3 className="font-bold text-lg text-lcars-pink uppercase tracking-wide">
              API Keys & Partner Access Tokens
            </h3>
          </div>
          <span className="text-xs text-[var(--foreground)]/50 font-mono">
            {apiKeys.length} Active Credentials
          </span>
        </div>

        {/* Newly Generated Key Alert */}
        {generatedKey && (
          <div className="bg-lcars-pink/15 border border-lcars-pink rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-lcars-pink font-bold text-sm">
              <Sparkles className="w-4 h-4" />
              <span>New API Secret Generated — Copy Immediately</span>
            </div>
            <p className="text-xs text-[var(--foreground)]/70">
              For security, this complete secret token will not be displayed again. Store it securely in your external client or integration config.
            </p>
            <div className="flex items-center gap-2">
              <input 
                type="text" 
                readOnly 
                value={generatedKey}
                className="flex-1 bg-black border border-lcars-pink/50 rounded px-3 py-2 text-xs font-mono text-lcars-pink"
              />
              <button 
                onClick={() => copyToClipboard(generatedKey, 'New API Secret')}
                className="lcars-pill bg-lcars-pink text-black font-bold px-4 py-2 text-xs"
              >
                COPY KEY
              </button>
            </div>
          </div>
        )}

        {/* Generate New Key Form */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 flex flex-col md:flex-row items-stretch md:items-end gap-3">
          <div className="flex-1">
            <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
              Application / Service Name
            </label>
            <input 
              type="text" 
              value={newKeyName} 
              onChange={e => setNewKeyName(e.target.value)}
              placeholder="e.g. Region 3 Roster Bot, Dallas Con Scanner"
              className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-pink"
            />
          </div>

          <div className="w-full md:w-48">
            <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
              Permission Scope
            </label>
            <select 
              value={newKeyScope} 
              onChange={e => setNewKeyScope(e.target.value as any)}
              className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-pink"
            >
              <option value="read-only">Read-Only (Events/Public)</option>
              <option value="sync-write">Sync-Write (STARFLEET Webhooks)</option>
              <option value="admin">Admin Full Access</option>
            </select>
          </div>

          <button 
            onClick={handleCreateApiKey}
            className="lcars-pill bg-lcars-pink text-black font-bold px-6 py-2.5 text-xs hover:bg-lcars-pink/80 whitespace-nowrap"
          >
            GENERATE NEW KEY
          </button>
        </div>

        {/* Existing Keys Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-white/10 text-[var(--foreground)]/40 font-mono uppercase tracking-wider">
                <th className="p-3">Partner / System</th>
                <th className="p-3">Key Prefix</th>
                <th className="p-3">Scope</th>
                <th className="p-3">Created</th>
                <th className="p-3">Last Active</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {apiKeys.map(k => (
                <tr key={k.id} className="hover:bg-white/5">
                  <td className="p-3 font-bold text-[var(--foreground)]">{k.name}</td>
                  <td className="p-3 font-mono text-lcars-tan">{k.keyPrefix}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase ${
                      k.scope === 'admin' 
                        ? 'bg-lcars-red/20 text-lcars-red' 
                        : k.scope === 'sync-write' 
                        ? 'bg-lcars-gold/20 text-lcars-gold' 
                        : 'bg-lcars-blue/20 text-lcars-blue'
                    }`}>
                      {k.scope}
                    </span>
                  </td>
                  <td className="p-3 text-[var(--foreground)]/50">{k.createdAt}</td>
                  <td className="p-3 font-mono text-[var(--foreground)]/70">{k.lastUsed || 'Never'}</td>
                  <td className="p-3 text-right">
                    <button 
                      onClick={() => {
                        setApiKeys(apiKeys.filter(item => item.id !== k.id));
                        toast.success(`Revoked API key: ${k.name}`);
                      }}
                      className="text-lcars-red hover:underline font-bold text-[0.7rem]"
                    >
                      REVOKE
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

    </div>
  );
}
